/** 全局常量。数值口径见 docs/ARCHITECTURE.md §3.3。 */

export const PROTOCOL_VERSION = 1 as const

/**
 * 客户端版本号，随 hello 上报。协议版本不变时，用它判断「这个客户端认不认识某个新玩法」：
 * 装在桌面上的 PWA 能无限期跑旧代码，旧版在短牌房里会按标准规则重算牌型、排错名次，还毫无提示。
 * 0.1.0 = 加入短牌之前的所有客户端（它们一直写死发这个值）。
 */
export const CLIENT_VERSION = '0.4.0'
// 0.4.0 = 「我的座位」面板（暂离 / 换座 / 起立 / 离开房间）、changeSeat、座位角标分清离线 / 暂离 /
//         筹码用完 / 买分中、审批列表的 AI 标记、向 AI 求购按它肯卖的上限。服务端不按版本拦截：
//         老页面没有这些入口，也不认识 PublicSeat.leaving，照常能打。
// 0.3.0 = 死按钮轮转、回来 / 新坐下下一手就发牌、底池按本手投入计、blindsPosted 带动作流水。
// 0.2.x 的页面仍能打（有 ante 时翻前底池显示会跳、盲注气泡要等快照），暂不拦截

/** 这个客户端能不能进短牌房（>= 0.2.0）。解析不了的版本号一律当旧版。 */
export function clientSupportsShortDeck(clientVersion: string): boolean {
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(clientVersion)
  if (m === null) return false
  const major = Number(m[1])
  const minor = Number(m[2])
  return major > 0 || minor >= 2
}

export const WS = {
  HELLO_TIMEOUT_MS: 5_000,
  CLIENT_PING_INTERVAL_MS: 15_000,
  CLIENT_DEAD_AFTER_MS: 35_000,
  SERVER_PING_INTERVAL_MS: 20_000,
  /** 硬性规则 #45：心跳超时必须 terminate() 而不是 close() */
  SERVER_TERMINATE_AFTER_MS: 45_000,
  MAX_PAYLOAD_BYTES: 16 * 1024,
  MAX_MSG_PER_SEC: 20,
  MAX_CONNECTIONS_PER_ACCOUNT: 4,
  OUT_BUFFER_FRAMES: 256,
  OUT_BUFFER_MS: 90_000,
  RESUME_GAP_SNAPSHOT_THRESHOLD: 20,
  BACKOFF_BASE_MS: 500,
  BACKOFF_FACTOR: 1.8,
  BACKOFF_CAP_MS: 30_000,
  BACKOFF_MAX_ATTEMPT: 6,
  BG_HARD_RECONNECT_MS: 20_000,
  FG_PROBE_TIMEOUT_MS: 3_000,
} as const

export const AUTH = {
  ACCESS_TOKEN_TTL_MS: 15 * 60_000,
  REFRESH_TOKEN_TTL_MS: 90 * 24 * 3_600_000,
  REFRESH_REPLAY_GRACE_MS: 10_000,
  SESSION_IDLE_TTL_MS: 30 * 60_000,
  /** 硬性规则 #52：KDF 用异步 crypto.scrypt，禁止 scryptSync */
  SCRYPT: { N: 1 << 15, r: 8, p: 1, keylen: 32 },
} as const

export const SEAT = {
  DISCONNECT_GRACE_MS: 90_000,
  /** 在线玩家基础思考时长 */
  ACTION_TIMEOUT_MS: 25_000,
  /** 断线玩家：不消耗时间银行（待拍板问题 1 的默认裁决） */
  ACTION_TIMEOUT_DISCONNECTED_MS: 10_000,
  /** 服务端多等的网络余量，对用户不可见 */
  ACTION_NETWORK_GRACE_MS: 1_500,
  /** 每手 1 次，仅 online 可用 */
  TIMEBANK_MS: 20_000,
  AUTO_FOLD_STREAK_TO_SITOUT: 3,
  SEAT_ABANDON_MS: 30 * 60_000,
} as const

export const ROOM_LIFECYCLE = {
  /* 10s：3 秒里推池动画就占掉大半，多赢家还要错峰播放，
     真正留给人「看清谁赢了」的时间几乎没有。 */
  INTER_HAND_DELAY_MS: 10_000,
  MUCK_WINDOW_MS: 3_000,
  SHOW_WINDOW_MS: 5_000,
  HOST_GRACE_MS: 120_000,
  /**
   * 没入座的观众断线后还算房间成员的时长。
   *
   * ★ 以前是 0：手机切个后台、电梯里丢一下信号，人就被从成员表里删掉，回来要重新进房。
   *   坐着的人有 SEAT.DISCONNECT_GRACE_MS（90s），观众没理由更苛刻 —— 取同一个值，
   *   一套心智模型：「断线 90 秒内回来，什么都没变」。
   */
  SPECTATOR_GRACE_MS: 90_000,
  EMPTY_TTL_MS: 5 * 60_000,
  ABANDON_TTL_MS: 30 * 60_000,
  MAX_LIFETIME_MS: 12 * 3_600_000,
  CODE_COOLDOWN_MS: 7 * 24 * 3_600_000,
  REAPER_INTERVAL_MS: 30_000,
  /**
   * 买分申请的存活时长：5 分钟无人处理即自动 `expired`。
   * 不设过期的话，房主一直没看到的申请会在队列里堆到天荒地老，
   * 而玩家那边的「等待审批」永远转圈——过期是给玩家一个明确的结论。
   */
  TOPUP_REQUEST_TTL_MS: 5 * 60_000,
  /**
   * 手牌之间「全员确认才开下一局」的兜底时长。
   *
   * ★ 它**不是**催人，而是防「等一个已经走了、但系统还以为他在」的人：
   *   断线与坐出都会把人移出应确认名单，但两者都有各自的判定延迟
   *   （DISCONNECT_GRACE_MS 90s / 心跳 45s），中间那段窗口里名单是脏的。
   *   没有这条兜底，一桌人会为一个不存在的人无限期停在结算浮层上。
   * 取 90s：比一次断线宽限略长，正常人看完结算早就点过继续了。
   */
  READY_CONFIRM_TIMEOUT_MS: 90_000,
} as const

export const RESUME = {
  SUMMARY_MAX_HANDS: 10,
  /** 断线不足 3s 且无错过手牌则不提示 */
  SILENT_RESUME_MS: 3_000,
} as const
